import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { rhm, apiError, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, statusTone } from "../ui";

const MOVEMENTS = ["purchase", "manufacturing", "return", "damage", "manual_adjustment", "transfer", "sale"];

export default function RhmInventory() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [adjust, setAdjust] = useState(null);
    const [form, setForm] = useState({ movement_type: "manual_adjustment", quantity: "", reason: "", batch_id: "" });

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ["rhm-inventory"],
        queryFn: async () => (await rhm.get("/inventory")).data,
    });
    const { data: alerts } = useQuery({
        queryKey: ["rhm-inventory-alerts"],
        queryFn: async () => (await rhm.get("/inventory/alerts")).data,
    });
    const { data: batches = [] } = useQuery({
        queryKey: ["rhm-batches", adjust?.product_id],
        queryFn: async () => (await rhm.get("/batches", { params: { product_id: adjust.product_id } })).data,
        enabled: !!adjust,
    });

    const submit = useMutation({
        mutationFn: async (payload) => (await rhm.post("/inventory/adjust", payload)).data,
        onSuccess: () => {
            toast.success("Stock adjusted");
            setAdjust(null);
            setForm({ movement_type: "manual_adjustment", quantity: "", reason: "", batch_id: "" });
            qc.invalidateQueries({ queryKey: ["rhm-inventory"] });
            qc.invalidateQueries({ queryKey: ["rhm-inventory-alerts"] });
            qc.invalidateQueries({ queryKey: ["rhm-dashboard"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-inventory-page">
            <PageHead title="Stock" sub="Batch aware inventory with a full movement ledger." />

            <div className="grid lg:grid-cols-3 gap-5 mb-6">
                <Card title="Low Stock" className="lg:col-span-1">
                    {alerts?.low_stock?.length ? alerts.low_stock.map((a) => (
                        <div key={a.product_id} className="border border-orange-500/30 bg-orange-500/5 p-3 mb-2">
                            <div className="text-sm font-semibold">{a.name}</div>
                            <div className="text-xs text-zinc-400 mt-1">
                                {a.available} left · min {a.min_stock} · reorder {a.suggested_reorder} {a.unit_label}s
                            </div>
                            <Link to="/rhm/purchase-orders" className="mono text-[9px] tracking-[0.16em] text-gold mt-2 inline-block">
                                CREATE PURCHASE ORDER →
                            </Link>
                        </div>
                    )) : <Empty msg="All products above minimum." />}
                </Card>
                <Card title="Out of Stock">
                    {alerts?.out_of_stock?.length ? alerts.out_of_stock.map((a) => (
                        <div key={a.product_id} className="border border-red-500/30 bg-red-500/5 p-3 mb-2">
                            <div className="text-sm font-semibold">{a.name}</div>
                            <div className="text-xs text-zinc-400 mt-1">Suggested reorder {a.suggested_reorder} {a.unit_label}s</div>
                        </div>
                    )) : <Empty msg="Nothing out of stock." />}
                </Card>
                <Card title="Expiring Soon">
                    {alerts?.expiring_soon?.length ? alerts.expiring_soon.slice(0, 6).map((b) => (
                        <div key={b.id} className="border border-white/10 p-3 mb-2">
                            <div className="text-sm font-semibold">{b.product_name}</div>
                            <div className="text-xs text-zinc-400 mt-1">
                                Batch {b.batch_number || "unnumbered"} · {b.days_left} days left · {b.available} units
                            </div>
                        </div>
                    )) : <Empty msg="No batch nearing expiry." />}
                </Card>
            </div>

            <Card title="Stock by Product">
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-inventory-table"
                        head={["Code", "Product", "On Hand", "Available", "Reserved", "Min", "Reorder", "State", ""]}
                        empty={!rows.length && <Empty msg="No products yet." />}>
                        {rows.map((r) => (
                            <tr key={r.product_id} data-testid={`rhm-inventory-row-${r.product_code}`}>
                                <Td className="mono text-xs text-gold">{r.product_code}</Td>
                                <Td>
                                    <Link to={`/rhm/products/${r.product_id}`} className="font-semibold hover:text-gold">{r.name}</Link>
                                    <div className="text-xs text-zinc-500">{r.division}</div>
                                </Td>
                                <Td>{r.on_hand}</Td>
                                <Td className="font-bold">{r.available}</Td>
                                <Td>{r.reserved}</Td>
                                <Td className="text-zinc-500">{r.min_stock}</Td>
                                <Td className="text-zinc-500">{r.reorder_qty}</Td>
                                <Td><Badge tone={statusTone(r.stock_state)}>{titleCase(r.stock_state)}</Badge></Td>
                                <Td>
                                    {can("inventory.edit") && (
                                        <button onClick={() => setAdjust(r)} data-testid={`rhm-adjust-${r.product_code}`}
                                            className="mono text-[10px] tracking-[0.16em] text-gold">ADJUST</button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!adjust} onClose={() => setAdjust(null)} title={`Adjust stock — ${adjust?.name || ""}`} testId="rhm-adjust-modal">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit.mutate({
                            product_id: adjust.product_id,
                            batch_id: form.batch_id || null,
                            movement_type: form.movement_type,
                            quantity: Number(form.quantity),
                            reason: form.reason,
                        });
                    }}
                    className="space-y-4"
                >
                    <Field label="Batch" hint="Leave as auto to use the first active batch">
                        <Select value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })}>
                            <option value="">Auto (first active batch)</option>
                            {batches.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {(b.batch_number || "unnumbered")} · {b.available} available
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Movement type" required>
                        <Select value={form.movement_type} onChange={(e) => setForm({ ...form, movement_type: e.target.value })}
                            options={MOVEMENTS} data-testid="adjust-type" />
                    </Field>
                    <Field label="Quantity change" required hint="Use a negative number for outward movement">
                        <Input type="number" required value={form.quantity}
                            onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="adjust-qty" />
                    </Field>
                    <Field label="Reason" required>
                        <Input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            placeholder="Stock count correction" data-testid="adjust-reason" />
                    </Field>
                    <div className="flex gap-3">
                        <Btn type="submit" disabled={submit.isPending} data-testid="adjust-submit">
                            {submit.isPending ? "SAVING…" : "POST ADJUSTMENT"}
                        </Btn>
                        <Btn type="button" variant="ghost" onClick={() => setAdjust(null)}>CANCEL</Btn>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
